import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Play, Pause, CaretLeft, CaretRight, X, SpeakerHigh, SpeakerSlash, ArrowClockwise, ArrowRight,
} from "@phosphor-icons/react";

// Scenes — read the landing TOP-TO-BOTTOM: brand → mission → six diamonds (in depth) → Big Picture.
// Captions kept in sync with backend/scripts/generate_tour_audio.py narration.
export const TOUR_SCENES = [
  {
    img: "/vv_logo.jpg", round: true,
    eyebrow: "Vintage Vantage",
    title: "List your finds. In record time.",
    caption: "List smarter, not harder.",
    audio: "/tour/scene1.mp3", ms: 8000,
  },
  {
    img: "/loop/compass.png",
    eyebrow: "The real adVantage",
    title: "When time stops being wasted.",
    caption: "The real adVantage is what becomes possible when time stops being wasted — and starts becoming a force multiplier.",
    audio: "/tour/scene2.mp3", ms: 9500,
  },
  {
    img: "/vv_logo.jpg", round: true,
    eyebrow: "The first seller-intelligence layer for the vintage market",
    title: "It all starts with six moves.",
    caption: "Built on a simple belief: the work you've already done shouldn't have to be done again. We don't just save time — we create opportunity.",
    bullets: ["Export your work", "Source smarter", "Draft instantly", "Build once", "Cross-list", "Compounded capability"],
    audio: "/tour/scene3.mp3", ms: 16000,
  },
  {
    diamond: 1,
    eyebrow: "01 · Export your work",
    title: "Bring the work you've already done.",
    caption: "Import your listings from virtually any marketplace, and we recover and rebuild the details you've already created — turning a limited CSV into a complete, seller-owned inventory.",
    audio: "/tour/scene4.mp3", ms: 13000,
  },
  {
    diamond: 2,
    eyebrow: "02 · Sell smarter",
    title: "Source smarter, right from the field.",
    caption: "Instant image search to identify, verify, and research a find right on-site — before you invest in a piece. Every sourcing decision, a confident one, in real time.",
    audio: "/tour/scene5.mp3", ms: 12000,
  },
  {
    diamond: 3,
    eyebrow: "03 · Draft instantly",
    title: "Every find, a finished draft.",
    caption: "Every new find becomes a fully structured draft — often before you've unpacked it. No blank page: review, add the last few unique details, and put your touch where it counts.",
    audio: "/tour/scene6.mp3", ms: 13000,
  },
  {
    diamond: 4,
    eyebrow: "04 · Build once",
    title: "Build one inventory you actually own.",
    caption: "Your inventory is the foundation of your business — your descriptions, your carefully taken photos, ready for every marketplace you sell on. Have drafts? Sales history? Same approach.",
    audio: "/tour/scene7.mp3", ms: 12000,
  },
  {
    diamond: 5,
    eyebrow: "05 · Cross-list",
    title: "List everywhere from a single record.",
    caption: "You're the master of your own vintage domain. Cross-list to the marketplaces you choose in minutes, multiplying the eyes on every piece — while your data stays portable, downloadable, and always yours.",
    audio: "/tour/scene8.mp3", ms: 14000,
  },
  {
    diamond: 6,
    eyebrow: "06 · Compounded capability",
    title: "Grow continuously, every lap.",
    caption: "Every listing, draft, find, and sale gives you seller-specific insights. Seasoned sellers sharpen their edge; newer sellers gain guidance that builds confidence. Everybody wins.",
    audio: "/tour/scene9.mp3", ms: 13000,
  },
  {
    img: "/vv_logo.jpg", round: true,
    eyebrow: "One promise, really",
    title: "Do the work once. Let it keep working.",
    caption: "Everywhere you sell, for as long as you sell — less busywork, and more of the judgment only you can bring.",
    audio: "/tour/scene10.mp3", ms: 11500,
  },
  {
    img: "/big_picture.png", wide: true,
    eyebrow: "The big picture",
    title: "Reduce the effort. Keep the agency.",
    caption: "List your fantastic finds — in record time.",
    audio: "/tour/scene11.mp3", ms: 10000,
  },
];

export default function StoryTour({ open, onClose, onFinishCta }) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [voiceOn, setVoiceOn] = useState(true);
  const [progress, setProgress] = useState(0);
  const [ended, setEnded] = useState(false);
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const startRef = useRef(0);

  const last = TOUR_SCENES.length - 1;
  const scene = TOUR_SCENES[index];

  const clearTimers = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearTimeout(timerRef.current);
    rafRef.current = null;
    timerRef.current = null;
  };

  const advance = useCallback(() => {
    setIndex((i) => {
      if (i >= last) {
        setPlaying(false);
        setEnded(true);
        return i;
      }
      return i + 1;
    });
  }, [last]);

  // Reset when (re)opened.
  useEffect(() => {
    if (open) { setIndex(0); setPlaying(true); setEnded(false); setProgress(0); }
  }, [open]);

  // Drive the current scene: audio (voice on) or a timer (voice off), plus the progress bar.
  useEffect(() => {
    if (!open || !playing || ended) return;
    clearTimers();
    setProgress(0);
    startRef.current = performance.now();
    const audio = audioRef.current;

    if (voiceOn && scene.audio && audio) {
      audio.src = scene.audio;
      audio.currentTime = 0;
      audio.onended = () => advance();
      const pr = audio.play();
      if (pr && pr.catch) pr.catch(() => {});
    }

    // Advancement safety net — audio 'onended' wins in voice mode; this guarantees we never stall.
    const budget = voiceOn && scene.audio ? scene.ms + 4000 : scene.ms;
    timerRef.current = setTimeout(() => advance(), budget);

    const tick = () => {
      let frac;
      if (voiceOn && audio && audio.currentTime > 0.05 && audio.duration > 0 && !isNaN(audio.duration)) {
        frac = audio.currentTime / audio.duration;
      } else {
        frac = (performance.now() - startRef.current) / scene.ms;
      }
      setProgress(Math.min(frac, 1));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      clearTimers();
      if (audio) { audio.onended = null; audio.pause(); }
    };
  }, [open, playing, ended, index, voiceOn, scene, advance]);

  const goto = (i) => { setEnded(false); setIndex(Math.max(0, Math.min(last, i))); setPlaying(true); };
  const restart = () => { setEnded(false); setIndex(0); setPlaying(true); };

  const close = () => {
    clearTimers();
    if (audioRef.current) audioRef.current.pause();
    onClose?.();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-[#0D1B2A] text-[#FDFBF7] flex flex-col vv-grain"
      data-testid="story-tour"
      role="dialog"
      aria-label="Watch the story tour"
    >
      <audio ref={audioRef} preload="auto" />

      {/* Progress segments (top) */}
      <div className="flex gap-1.5 px-4 pt-4" data-testid="tour-progress">
        {TOUR_SCENES.map((_, i) => (
          <div key={i} className="h-1 flex-1 bg-white/15 overflow-hidden rounded-full">
            <div
              className="h-full bg-[#B47A3C]"
              style={{ width: i < index ? "100%" : i === index ? `${progress * 100}%` : "0%", transition: i === index ? "none" : "width 0.2s" }}
            />
          </div>
        ))}
      </div>

      {/* Scene stage */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center overflow-hidden">
        <div key={index} className="vv-fade-up flex flex-col items-center max-w-2xl">
          <div className="mb-8 flex items-center justify-center">
            {scene.diamond ? (
              <div className="relative w-28 h-28 sm:w-36 sm:h-36 flex items-center justify-center" data-testid="tour-diamond">
                <div className="absolute inset-0 rotate-45 bg-[#B47A3C] shadow-2xl" />
                <span className="relative font-serif-display text-5xl sm:text-6xl font-bold text-[#0D1B2A]">{scene.diamond}</span>
              </div>
            ) : (
              <img
                src={scene.img}
                alt=""
                className={`object-contain drop-shadow-2xl ${
                  scene.round
                    ? "w-28 h-28 sm:w-36 sm:h-36 rounded-full ring-2 ring-[#B47A3C]/40"
                    : scene.wide
                    ? "w-full max-w-2xl max-h-[42vh] border border-[#B47A3C]/30 bg-white/5"
                    : "w-28 h-28 sm:w-36 sm:h-36"
                }`}
              />
            )}
          </div>
          <p className="label-caps mb-4" style={{ color: "#B47A3C", letterSpacing: "0.22em" }} data-testid="tour-eyebrow">
            {scene.eyebrow}
          </p>
          <h2 className="font-serif-display text-3xl sm:text-5xl leading-[1.06] font-medium tracking-tight mb-5" data-testid="tour-title">
            {scene.title}
          </h2>
          <p className="text-lg sm:text-xl text-[#D8D3C8] leading-relaxed max-w-xl" data-testid="tour-caption">
            {scene.caption}
          </p>

          {scene.bullets && (
            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3 text-left" data-testid="tour-bullets">
              {scene.bullets.map((b) => (
                <li key={b} className="flex items-center gap-3">
                  <span className="shrink-0 w-2.5 h-2.5 rotate-45 bg-[#B47A3C]" />
                  <span className="text-[15px] sm:text-base text-[#FDFBF7] font-semibold">{b}</span>
                </li>
              ))}
            </ul>
          )}

          {scene.freestyle && (
            <p className="mt-6 text-sm text-[#8A867D] italic" data-testid="tour-freestyle-note">
              ✦ Your closing words go here
            </p>
          )}

          {ended && (
            <div className="mt-9 flex flex-col sm:flex-row items-center gap-3" data-testid="tour-end-cta">
              <button
                onClick={() => { close(); onFinishCta?.(); }}
                data-testid="tour-get-started"
                className="h-13 px-7 py-3 bg-[#FDFBF7] text-[#0D1B2A] rounded-none font-semibold text-[17px] inline-flex items-center gap-2 hover:bg-white transition-transform active:scale-95"
              >
                Get started free <ArrowRight size={18} weight="bold" />
              </button>
              <button
                onClick={restart}
                data-testid="tour-replay"
                className="h-13 px-7 py-3 border border-[#FDFBF7]/40 rounded-none font-medium inline-flex items-center gap-2 hover:bg-white/10 transition-colors"
              >
                <ArrowClockwise size={18} weight="bold" /> Watch again
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Controls — ALL at the bottom (reachable one-handed) */}
      <div className="border-t border-white/10 bg-black/20 px-4 py-4 sm:py-5" data-testid="tour-controls">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-2">
          <button onClick={close} data-testid="tour-close" aria-label="Close tour"
            className="h-14 w-14 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
            <X size={26} weight="bold" />
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => goto(index - 1)} disabled={index === 0} data-testid="tour-prev" aria-label="Previous"
              className="h-14 w-14 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors disabled:opacity-30">
              <CaretLeft size={26} weight="bold" />
            </button>
            <button onClick={() => (ended ? restart() : setPlaying((p) => !p))} data-testid="tour-playpause" aria-label="Play or pause"
              className="h-16 w-16 flex items-center justify-center rounded-full bg-[#B47A3C] text-[#0D1B2A] hover:bg-[#c98e4e] transition-colors active:scale-95">
              {ended ? <ArrowClockwise size={28} weight="fill" /> : playing ? <Pause size={30} weight="fill" /> : <Play size={30} weight="fill" />}
            </button>
            <button onClick={() => goto(index + 1)} disabled={index === last} data-testid="tour-next" aria-label="Next"
              className="h-14 w-14 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors disabled:opacity-30">
              <CaretRight size={26} weight="bold" />
            </button>
          </div>
          <button onClick={() => setVoiceOn((v) => !v)} data-testid="tour-voice-toggle" aria-label="Toggle voice-over"
            className="h-14 w-14 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
            {voiceOn ? <SpeakerHigh size={26} weight="fill" className="text-[#B47A3C]" /> : <SpeakerSlash size={26} weight="bold" />}
          </button>
        </div>
        <p className="text-center text-xs text-[#8A867D] mt-3">
          {voiceOn ? "Narrated · tap the speaker to mute" : "Captions only · tap the speaker for voice-over"}
        </p>
      </div>
    </div>,
    document.body
  );
}
